'use client';

import { AddToCartPage } from '@features/add-to-cart-page/api/AddToCartPageRepositoryInterface';
import { mapAddToCartToSteps } from '@features/add-to-cart-page/utils/mutiStepAddToCart';
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';

type Props = {
  addToCartPage: AddToCartPage;
  isAddedAsSubscription: boolean;
  isBusinessCustomer: boolean;
  isCoolblueApp: boolean;
  children: ReactNode;
};

const AddToCartStepsContext = createContext<
  | {
      steps: ReturnType<typeof mapAddToCartToSteps>;
      nextStep: () => void;
      prevStep: () => void;
      selectedStep: number;
      hasMultiStep: boolean;
    }
  | undefined
>(undefined);

export function AddToCartStepsProvider({
  addToCartPage: addToCartPageProps,
  isAddedAsSubscription,
  isBusinessCustomer,
  isCoolblueApp,
  children
}: Props) {
  // To prevent rerender of the ATC sections when shopping cart is mutated
  // We are memoizing the first render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const addToCartPage = useMemo(() => addToCartPageProps, []);
  const steps = mapAddToCartToSteps(
    addToCartPage,
    isAddedAsSubscription,
    isBusinessCustomer,
    isCoolblueApp
  );

  const [currentStep, setCurrentStep] = useState(0);

  function nextStep() {
    setCurrentStep(prev => prev + 1);
  }

  function prevStep() {
    setCurrentStep(prev => prev - 1);
  }

  return (
    <AddToCartStepsContext.Provider
      value={{
        steps,
        selectedStep: currentStep,
        nextStep,
        prevStep,
        hasMultiStep: steps.length > 1
      }}
    >
      {children}
    </AddToCartStepsContext.Provider>
  );
}

export function useAddToCartSteps() {
  const context = useContext(AddToCartStepsContext);

  if (!context) {
    throw new Error('useAddToCartSteps must be used within a AddToCartStepsProvider');
  }

  return context;
}
